import { Menu } from '@systemeio/components';
import { ChevronDownIcon } from '@systemeio/icons';
import { UserWorkspaceRolesEnum } from '@systemeio/types';
import NProgress from 'nprogress';
import { useEffect, useMemo, useState } from 'react';
import { setWorkspace } from 'shared/api/workspace-change-api';
import { SelectorField, SelectorItem, selectorPopupClassName, selectorTriggerClassName } from 'shared/components/navbar/components/selector-item';
import { pageRoute } from 'shared/constants/routes.constants';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { META_DATA_FROM_SERVER_KEY } from 'shared/hooks/use-metadata-from-server';
import { useRouter } from 'shared/hooks/use-router';
import useUser from 'shared/hooks/use-user';
import { hasWorkspaces } from 'shared/hooks/use-user/utils/has-workspaces';
import { getPageRoute } from 'shared/utils/get-dynamic-page-route';
import { useSWRConfig } from 'swr';
import { twJoin } from 'tailwind-merge';
import { toast } from '../../toast';
interface WorkspaceDataInterface {
  id: number | null;
  image: string;
  name: string;
  role: UserWorkspaceRolesEnum | null;
}
interface WorkspaceSelectorProps {
  isDesktop: boolean;
}
export function WorkspaceSelector({
  isDesktop
}: WorkspaceSelectorProps) {
  const {
    user,
    avatarUrl
  } = useUser();
  const router = useRouter();
  const [id, setId] = useState<number | null>(null);
  const {
    mutate
  } = useSWRConfig();
  const {
    t
  } = useLocoTranslation();
  const [isFetching, setIsFetching] = useState(false);
  useEffect(() => {
    if (!hasWorkspaces(user)) return;
    const selectedWorkspace = user.workspaceMemberships.find(el => el.workspaceOwner.id === user?.workspaceOwner);
    setId(selectedWorkspace?.id ?? null);
  }, [user]);
  const workspaces = useMemo(() => {
    if (!user) return;
    const arr: WorkspaceDataInterface[] = [];
    if (user.subdomain) {
      arr.push({
        id: null,
        name: user.subdomain,
        image: avatarUrl ?? '',
        role: null
      });
    }
    if (hasWorkspaces(user)) {
      user.workspaceMemberships.forEach(workspace => {
        arr.push({
          id: workspace.id,
          name: workspace.name,
          image: workspace.image,
          role: workspace.role
        });
      });
    }
    return arr;
  }, [avatarUrl, user]);
  const redirectToPath = async (route: string) => {
    if (router.pathname === route) return;
    await router.replace(route);
  };
  const clearSWRData = async () => {
    await mutate(key => key !== META_DATA_FROM_SERVER_KEY, undefined, {
      revalidate: false
    });
  };
  const revalidateSWRData = async () => {
    await mutate(key_0 => key_0 !== META_DATA_FROM_SERVER_KEY);
  };
  const getWorkspaceRedirectRoute = (role: UserWorkspaceRolesEnum | null) => {
    if (role === UserWorkspaceRolesEnum.workspaceAssistant) {
      return getPageRoute(pageRoute.Funnels);
    }
    return getPageRoute(pageRoute.Dashboard);
  };
  const onWorkspaceChange = async (workspaceId: number | null) => {
    if (workspaceId === id) return;
    const workspace_0 = workspaces?.find(w => w.id === workspaceId);
    if (!workspace_0) return;
    try {
      setIsFetching(true);
      NProgress.start();
      await setWorkspace({
        currentWorkspaceMembership: workspaceId
      });
      await clearSWRData();
      await redirectToPath(getWorkspaceRedirectRoute(workspace_0.role));
      await revalidateSWRData();
      toast.success(t('dashboard.notifications.workspace_saved'));
    } catch {
      toast.error(t('global.error'));
    } finally {
      setIsFetching(false);
      NProgress.done();
    }
  };
  if (!user || !workspaces || !hasWorkspaces(user)) return null;
  const selectedWorkspace_0 = workspaces.find(el_0 => el_0.id === id);
  if (!selectedWorkspace_0) return null;
  const label = t('header.user.profile.menu.workspace-title');
  return <SelectorField isDesktop={isDesktop} label={label}>
      <Menu disabled={isFetching} drawerTitle={t('header.user.profile.menu.workspace')} className={selectorPopupClassName} trigger={() => <>
            <SelectorItem src={selectedWorkspace_0.image} displayName={selectedWorkspace_0.name} />
            <ChevronDownIcon className="transition-base text-field-icon size-5 shrink-0" />
          </>} triggerProps={{
      'aria-label': label,
      className: twJoin(selectorTriggerClassName, isFetching && 'animate-pulse'),
      'data-test-id': 'workspace-selector-button'
    }} items={workspaces.map(workspace_1 => ({
      id: String(workspace_1.id),
      state: workspace_1.id === selectedWorkspace_0.id ? 'active' : 'default',
      label: <SelectorItem isOption displayName={workspace_1.name} src={workspace_1.image} />,
      onSelect: () => onWorkspaceChange(workspace_1.id)
    }))} />
    </SelectorField>;
}